import { useState, useRef } from "react";
import {
  motion,
  useInView,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ContextBadge from "../ContextBadge";
import MusicPlayer from "../MusicPlayer";
import { musicAlbums } from "../../data/creativeData";
import { CREATIVE_VARIANTS } from "./creative/SharedCreative";
import AuthorPhoto from "../../assets/AuthorPhoto.jpg";
import "../../styles/CreativeMode.css";

// Import game covers
import KotchCover from "../Kotch/cover.png";
import BountifulSeaCover from "../BountifulSea/cover.png";
import SandsOfExileCover from "../SandsOfExile/cover.png";

// Import paintings
import Painting1 from "../../assets/Paintings/Crop20181015_105311.jpg";
import Painting2 from "../../assets/Paintings/Crop20181015_105323.jpg";
import Painting3 from "../../assets/Paintings/Crop20181015_105332.jpg";
import Painting4 from "../../assets/Paintings/Crop20181015_105342.jpg";
import Painting5 from "../../assets/Paintings/Crop20181015_105445.jpg";
import Painting6 from "../../assets/Paintings/Crop20181015_105511.jpg";
import Painting7 from "../../assets/Paintings/Crop20181015_105520.jpg";
import Painting8 from "../../assets/Paintings/Crop20181015_105529.jpg";
import Painting9 from "../../assets/Paintings/Crop20181015_105632.jpg";
import Painting10 from "../../assets/Paintings/Crop20190412_143853.jpg";
import Painting11 from "../../assets/Paintings/Crop20191212_121100.jpg";
import Painting12 from "../../assets/Paintings/Crop20191222_165203.jpg";
import Painting13 from "../../assets/Paintings/Crop20200115_091924.jpg";
import Painting14 from "../../assets/Paintings/Crop20200516_195759.jpg";
import Painting15 from "../../assets/Paintings/20201221_090743Crop.jpg";
import Painting16 from "../../assets/Paintings/20210404_162124.jpg";
import Painting17 from "../../assets/Paintings/IMG_20220104_043017_643.jpg";
import Painting18 from "../../assets/Paintings/20190412_143904.jpg";
import Painting19 from "../../assets/Paintings/20190509_103003.jpg";

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" as const },
  },
};

// Parallax Background Component
const ParallaxBackground: React.FC = () => {
  const { scrollYProgress } = useScroll();

  // Stars move very slowly (subtle)
  const starsY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);
  // Diamond pattern moves much faster (dramatic parallax)
  const patternY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const patternOpacity = useTransform(
    scrollYProgress,
    [0, 0.3, 1],
    [1, 0.7, 0.4]
  );

  return (
    <div className="archive-background">
      <div className="bg-base" />
      <div className="bg-gradient-animated" />
      <motion.div className="bg-stars" style={{ y: starsY }} />
      <motion.div
        className="bg-pattern"
        style={{ y: patternY, opacity: patternOpacity }}
      />
      <div className="bg-particles">
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} className="particle" />
        ))}
      </div>
      <div className="bg-noise" />
      <div className="bg-vignette" />
    </div>
  );
};

// Scroll-reveal section
const RevealSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      id={id}
      className={`archive-section ${className}`}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={sectionVariants}
    >
      {children}
    </motion.section>
  );
};

// Flippable World Card Component
const WorldCard: React.FC<{
  world: {
    id: string;
    name: string;
    subtitle?: string;
    description: string;
    features: { title: string; description: string }[];
    link?: { url: string; label: string };
  };
}> = ({ world }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <div
      className={`world-card ${isFlipped ? "flipped" : ""}`}
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <div className="card-inner">
        <div className="card-front">
          <div className="world-emblem">◈</div>
          <h3 className="world-name">{world.name}</h3>
          {world.subtitle && (
            <span className="world-subtitle">{world.subtitle}</span>
          )}
          <p className="world-brief">{world.description}</p>
          <span className="flip-hint">Click to explore</span>
        </div>
        <div className="card-back">
          <h4 className="world-name-back">{world.name}</h4>
          <div className="world-features">
            {world.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <span className="feature-title">{feature.title}</span>
                <span className="feature-desc">{feature.description}</span>
              </div>
            ))}
          </div>
          {world.link && (
            <a
              href={world.link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="world-link"
              onClick={(e) => e.stopPropagation()}
            >
              {world.link.label} →
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Lazy loading image component
const LazyImage: React.FC<{
  src: string;
  alt: string;
  onClick: () => void;
}> = ({ src, alt, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(imgRef, { once: true, margin: "100px" });

  return (
    <div ref={imgRef} className="painting-frame" onClick={onClick}>
      {isInView && (
        <>
          {!isLoaded && <div className="painting-placeholder">Loading...</div>}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            style={{ opacity: isLoaded ? 1 : 0 }}
          />
        </>
      )}
      {!isInView && <div className="painting-placeholder">◇</div>}
    </div>
  );
};

// Fullscreen Image Viewer
const ImageViewer: React.FC<{
  images: { src: string; title: string }[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}> = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  return (
    <motion.div
      className="image-viewer-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button className="viewer-close" onClick={onClose}>
        ×
      </button>
      <button
        className="viewer-nav viewer-prev"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
      >
        ‹
      </button>
      <button
        className="viewer-nav viewer-next"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
      >
        ›
      </button>
      <motion.div
        className="viewer-content"
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
      >
        <img src={images[currentIndex].src} alt={images[currentIndex].title} />
        <p className="viewer-title">{images[currentIndex].title}</p>
      </motion.div>
    </motion.div>
  );
};

const CreativeMode: React.FC = () => {
  const navigate = useNavigate();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // World data - Real worlds
  const worlds = [
    {
      id: "osgara",
      name: "Osgara",
      subtitle: "The World of Timeless' Aria",
      description:
        "An experiment in worldbuilding through narrative—watching how a rich, lore-filled world reacts to cataclysmic change and fights for its own survival.",
      features: [
        {
          title: "Living Worldbuilding",
          description:
            "The world evolves through the story, shaped by its own history",
        },
        {
          title: "Published Works",
          description:
            "Two ~700-page novels and multiple novellas, co-written with a friend",
        },
        {
          title: "Stakes of Existence",
          description:
            "Will the world survive its own transformation, or collapse?",
        },
      ],
      link: { url: "https://www.timelessaria.com", label: "Explore Osgara" },
    },
    {
      id: "tanaar",
      name: "Tanaar",
      subtitle: "A D&D Open World",
      description:
        "A true open-world D&D setting built from the ground up—complete with cosmology, pantheons, and a campaign where mortals may challenge the gods themselves.",
      features: [
        {
          title: "The Council of 100",
          description:
            "100 nations, each with unique cultures, races, capitals, symbols (2017, pre-AI)",
        },
        {
          title: "~70 Recorded Sessions",
          description: "A living campaign with years of actual play documented",
        },
        {
          title: "Trial by Fire: Deicide",
          description:
            "A campaign where the fate of the scales hangs in balance",
        },
      ],
    },
    {
      id: "caiden",
      name: "Caiden",
      subtitle: "Collaborative Worldbuilding",
      description:
        "Assisting my brother in crafting his D&D world—contributing deep lore documents, in-world histories, and short stories that foreshadow events to come.",
      features: [
        {
          title: "Deep Lore Documents",
          description: "Comprehensive in-world texts and historical records",
        },
        {
          title: "In-World Fiction",
          description: "Short stories written from within the world itself",
        },
        {
          title: "Urkaiden Tales",
          description: "Stories that hint at greater mysteries yet to unfold",
        },
      ],
      link: { url: "/story/short-stories", label: "Read the Stories" },
    },
    {
      id: "bereda",
      name: "Bereda",
      subtitle: "The First World (2006)",
      description:
        "My first passion project, begun in 2006. A world with unique creation myths that grew too vast to contain—now resting, waiting for its time.",
      features: [
        {
          title: "Unique Cosmology",
          description: "Creation myths that diverge from traditional fantasy",
        },
        {
          title: "Years of Development",
          description:
            "Built and expanded from 2006 until it outgrew its bounds",
        },
        {
          title: "Dormant, Not Dead",
          description:
            "The world still exists, waiting for its story to be told",
        },
      ],
    },
  ];

  // Books data - Real books
  const books = [
    {
      title: "Timeless' Aria: The Sapphire Shore",
      status: "Novel",
      link: "https://www.amazon.com/Sapphire-Shore-Timeless-Aria-Book-ebook/dp/B0CNP3RNC5",
    },
    {
      title: "Timeless' Aria: The Ember Tide",
      status: "Novel",
      link: "https://www.amazon.de/dp/3911111207?bestFormat=true",
    },
    {
      title: "Reports of Rebellion",
      status: "Novella",
      link: "https://www.timelessaria.com/novella",
      free: true,
    },
    {
      title: "Heart of Gold",
      status: "Novella",
      link: "https://www.amazon.de/dp/B0CR7VV32Y",
    },
    {
      title: "Scoundrels!",
      status: "Novella",
      link: "https://www.amazon.de/dp/B0CXNT99B9",
    },
    {
      title: "Juniara: Last of the Free",
      status: "Novella",
      link: "https://www.amazon.de/dp/3911111185",
    },
    {
      title: "Hatchling of the Orchard",
      status: "Novella",
      link: "https://www.timelessaria.com/hatchling",
      free: true,
    },
  ];

  // Game design videos (YouTube) - Level design & cinematics in Unreal Engine
  const gameDesignVideos = [
    { id: "Zti-1PIOts8", title: "Game Design" },
    { id: "mfl8MYu2fQk", title: "Game Design" },
    { id: "v0X1w6-MvTY", title: "Game Design" },
    { id: "FEwkVX-4W-c", title: "Game Design" },
    { id: "sf4q-CGqooQ", title: "Game Design" },
    { id: "kOpecsomGVE", title: "Game Design" },
    { id: "jCwHFtA3VeY", title: "Game Design" },
    { id: "9xpBJmvbbNg", title: "Game Design" },
    { id: "RUM9riXGBas", title: "Game Design" },
    { id: "WMvT0ok_i8Y", title: "Game Design" },
    { id: "umult8wqA2w", title: "Game Design" },
    { id: "nNqhZzh41F4", title: "Game Design" },
    { id: "3AkML0j4-14", title: "Game Design" },
    { id: "DwJRYvJyBCA", title: "Game Design" },
    { id: "Gm-RMysWHyI", title: "Game Design" },
    { id: "A3LEds9tkfE", title: "Game Design" },
    { id: "50CPNp4u-iw", title: "Game Design" },
    { id: "AoZywEhoc-M", title: "Game Design" },
    { id: "LCEApAWxbpI", title: "Game Design" },
  ];

  // Painting process videos (YouTube)
  const paintingVideos = [
    { id: "8IfnjLIWBBo", title: "Painting Process" },
    { id: "wlsrWPAnO4E", title: "Painting Process" },
    { id: "lij0TB-H3AU", title: "Painting Process" },
    { id: "Ixw_0BdajgQ", title: "Painting Process" },
    { id: "6wsk04pMTkQ", title: "Painting Process" },
    { id: "Jw_2EClcftk", title: "Painting Process" },
    { id: "r8iAQNrb7TM", title: "Painting Process" },
    { id: "5DVsAps7fKQ", title: "Painting Process" },
    { id: "-4qkpVi0onQ", title: "Painting Process" },
  ];

  // Paintings data
  type Medium = "Oils" | "Acrylics";
  const paintings: { src: string; title: string; medium: Medium }[] = [
    { src: Painting16, title: "The Migoran Peaks", medium: "Oils" },
    { src: Painting14, title: "Lost in the Mist", medium: "Oils" },
    { src: Painting12, title: "Northern Summer", medium: "Oils" },
    { src: Painting11, title: "Untrodden Path", medium: "Oils" },
    { src: Painting10, title: "The Well of Life", medium: "Oils" },
    { src: Painting18, title: "Ashen Folk", medium: "Oils" },
    { src: Painting17, title: "For Franzi", medium: "Oils" },
    { src: Painting15, title: "For Frank, and the EasternGraphics Family", medium: "Oils" },
    { src: Painting13, title: "For Lee", medium: "Oils" },
    { src: Painting19, title: "Watercolors & Acrylics", medium: "Acrylics" },
    { src: Painting9, title: "Motra", medium: "Oils" },
    { src: Painting8, title: "Furled Sails (Duh)", medium: "Oils" },
    { src: Painting7, title: "The Blockade of Contara", medium: "Oils" },
    { src: Painting6, title: "Sea of Scoundrels", medium: "Oils" },
    { src: Painting5, title: "Attempt 1", medium: "Oils" },
    { src: Painting4, title: "The World of Osgara", medium: "Acrylics" },
    { src: Painting3, title: "Sebriga", medium: "Acrylics" },
    { src: Painting2, title: "Around King's Range", medium: "Acrylics" },
    { src: Painting1, title: "Giantsfall Tavern", medium: "Acrylics" },
  ];

  // Painting filter state
  const [paintingFilter, setPaintingFilter] = useState<Medium | "All">("All");

  const filteredPaintings = paintingFilter === "All"
    ? paintings
    : paintings.filter(p => p.medium === paintingFilter);

  const openViewer = (index: number) => {
    setCurrentImageIndex(index);
    setViewerOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % filteredPaintings.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + filteredPaintings.length) % filteredPaintings.length
    );
  };

  return (
    <div className="creative-mode">
      {/* Parallax Background */}
      <ParallaxBackground />

      {/* Back Button + Reroll */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
      >
        <Link to="/" className="back-button">
          <span className="back-arrow">←</span>
          <span>Back</span>
        </Link>
        <button
          onClick={() => {
            const others = CREATIVE_VARIANTS.filter((v) => v !== "/creative/archive");
            navigate(others[Math.floor(Math.random() * others.length)]);
          }}
          title="Random variant"
          style={{
            background: "none",
            border: "1px solid currentColor",
            borderRadius: "50%",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "inherit",
            opacity: 0.5,
            transition: "opacity 0.2s, transform 0.2s",
            fontSize: "0.85rem",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.transform = "rotate(180deg)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.5";
            e.currentTarget.style.transform = "rotate(0deg)";
          }}
        >
          ⟳
        </button>
      </motion.div>

      {/* Main Content */}
      <div className="archive-container">
        {/* ═══════════════════════════════════════════════════════════
           THRESHOLD - Opening Section
           ═══════════════════════════════════════════════════════════ */}
        <motion.section
          className="threshold-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="threshold-content">
            {/* Creative Photo Frame */}
            <motion.div
              className="creative-photo-frame"
              initial={{ scale: 0.8, opacity: 0, rotate: -5 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.8, type: "spring" }}
            >
              <div className="photo-ornament top-left">◈</div>
              <div className="photo-ornament top-right">◈</div>
              <div className="photo-ornament bottom-left">◈</div>
              <div className="photo-ornament bottom-right">◈</div>
              <img src={AuthorPhoto} alt="Mostafa Elbehery" />
            </motion.div>

            <motion.h1
              className="archive-title creative-name"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Mostafa Elbehery
            </motion.h1>

            <motion.div
              className="creative-subtitle"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <span className="subtitle-role">Writer</span>
              <span className="subtitle-divider">/</span>
              <span className="subtitle-role">World Builder</span>
              <span className="subtitle-divider">/</span>
              <span className="subtitle-role">Level Designer</span>
              <span className="subtitle-divider">/</span>
              <span className="subtitle-role">Graphics Enthusiast</span>
              <span className="subtitle-divider">/</span>
              <span className="subtitle-role">Hobbyist Artist</span>
              <span className="subtitle-divider">/</span>
              <span className="subtitle-role">And More!</span>
            </motion.div>

            <motion.div
              className="archive-emblem"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              ◈
            </motion.div>

            <motion.p
              className="archive-quote"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              "Knowledge is limited. Imagination encircles the world."
              <span className="quote-author">— Albert Einstein</span>
            </motion.p>

            <motion.div
              className="scroll-prompt"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
            >
              <span>Scroll to explore</span>
              <span className="scroll-arrow">↓</span>
            </motion.div>
          </div>
        </motion.section>

        {/* ═══════════════════════════════════════════════════════════
           WORLDS - Primary Passion
           ═══════════════════════════════════════════════════════════ */}
        <RevealSection id="worlds" className="worlds-section">
          <div className="section-header">
            <span className="section-number">I</span>
            <h2 className="section-title">World Creation</h2>
            <p className="section-subtitle">
              It is, I find, the single most engrossing thing to dream up a new
              world and attempt to breathe life and character into it while
              maintaining coherence with its inner laws.
            </p>
          </div>

          <div className="worlds-grid">
            {worlds.map((world) => (
              <WorldCard key={world.id} world={world} />
            ))}
          </div>

          <p className="section-epilogue">
            World-building, I feel, forces you to think about everything.
          </p>
        </RevealSection>

        {/* ═══════════════════════════════════════════════════════════
           WRITING - Books & Chronicles
           ═══════════════════════════════════════════════════════════ */}
        <RevealSection id="writing" className="writing-section">
          <div className="section-header">
            <span className="section-number">II</span>
            <h2 className="section-title">
              Published Work{" "}
              <ContextBadge
                variant="creative"
                context="I make very little effort to market these publications, as I write them primarily for myself but I do dream of one day taking them to a publisher and seeing just how far my reach for the stars would go."
              />
            </h2>
            <p className="section-subtitle">More to come!</p>
          </div>

          <div className="books-shelf">
            {books.map((book, index) => (
              <motion.a
                key={index}
                href={book.link}
                target="_blank"
                rel="noopener noreferrer"
                className="book-spine"
                whileHover={{ x: 10, transition: { duration: 0.2 } }}
              >
                <div className="spine-content">
                  <span className="book-title">{book.title}</span>
                  <div className="book-chips">
                    {book.free && <span className="book-free">Free</span>}
                    <span
                      className={`book-status ${book.status.toLowerCase()}`}
                    >
                      {book.status}
                    </span>
                  </div>
                </div>
                <span className="book-arrow">→</span>
              </motion.a>
            ))}
          </div>

          <div className="stories-link">
            <Link to="/story/short-stories" className="drawer-link">
              <span>Explore Short Stories</span>
              <span className="link-arrow">→</span>
            </Link>
          </div>
        </RevealSection>

        {/* ═══════════════════════════════════════════════════════════
           VISUAL ARTS - Oil Paintings
           ═══════════════════════════════════════════════════════════ */}
        <RevealSection id="visual" className="visual-section">
          <div className="section-header">
            <span className="section-number">III</span>
            <h2 className="section-title">Paintings</h2>
            <p className="section-subtitle">
              Inspired by Bob Ross and how he made painting seem so wonderfully
              easy, I picked up my first set of Acrylics and never looked back
              as I progressed through Water Colours and eventually to Oils.
            </p>
          </div>

          <div className="paintings-filter">
            {(["All", "Oils", "Acrylics"] as const).map((filter) => (
              <button
                key={filter}
                type="button"
                className={`filter-button ${paintingFilter === filter ? "active" : ""}`}
                onClick={() => setPaintingFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="paintings-gallery">
            {filteredPaintings.map((painting, index) => (
              <LazyImage
                key={painting.title}
                src={painting.src}
                alt={painting.title}
                onClick={() => openViewer(index)}
              />
            ))}
          </div>

          <div className="video-gallery-header">
            <h3 className="video-gallery-title">Painting Process Videos</h3>
            <p className="video-gallery-subtitle">
              Watch the journey from blank canvas to finished piece
            </p>
          </div>

          <div className="video-gallery">
            {paintingVideos.map((video, index) => (
              <div key={index} className="video-frame">
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </RevealSection>

        {/* ═══════════════════════════════════════════════════════════
           GAMES - Interactive Experiences
           ═══════════════════════════════════════════════════════════ */}
        <RevealSection id="games" className="games-section">
          <div className="section-header">
            <span className="section-number">IV</span>
            <div className="section-title-row">
              <h2 className="section-title">Game Design</h2>
              {/* <ContextBadge
                variant="creative"
                context="I have a saying about games from many hours spent designing homebrew for D&D and private projects yet to be released: They are best when they are realistic but not constrained by realism. What I mean by that is that I've found that simulationist attempts at mimicking reality to the smallest of details often get lost in the pursuit of the simulation rather than providing fun, however games that do away with being realistic altogether can lose a sense of groundedness that helps players relate. One game that straddles this line well is Factorio, where the Engineer can somehow carry many tons while running around crafting nuclear reactors by hand, which is a clear abstraction of that part of reality that the game wishes not to burden the player with or concern itself with. Instead it is merely realistic in the chains of production, they make enough sense to feel immersive, but are not incredibly mundane that they become boring."
              /> */}
              {/* <ContextBadge
                variant="creative"
                context="I've begun some implementations of these games both in UE & Godot, but the ones "
              /> */}
            </div>
            <p className="section-subtitle">
              These are games made for a Fantasy World, in an attempt to convey
              a slice of what the people of those worlds would have done for
              their own entertainment as I find it can be telling of what kind
              of people they were. These games are meant to be playable
              physically, but I've adapted them here out of a desire to play
              them digitally.
            </p>
          </div>

          <div className="games-grid">
            <Link to="/kotch" className="game-card">
              <div className="game-card-image">
                <img src={KotchCover} alt="Kotch" />
              </div>
              <div className="game-card-content">
                <div className="game-card-header">
                  <h3 className="game-card-title">Kotch</h3>
                  <span className="game-year-chip">2018</span>
                </div>
                <p className="game-card-description">
                  Strategic 3-lane card game
                </p>
                <span className="game-card-play">Play →</span>
              </div>
            </Link>

            <Link to="/bountiful-sea" className="game-card">
              <div className="game-card-image">
                <img src={BountifulSeaCover} alt="Bountiful Sea" />
              </div>
              <div className="game-card-content">
                <div className="game-card-header">
                  <h3 className="game-card-title">Bountiful Sea</h3>
                  <span className="game-year-chip">2023</span>
                </div>
                <p className="game-card-description">
                  Bluffing dice game of deception
                </p>
                <span className="game-card-play">Play →</span>
              </div>
            </Link>

            <Link to="/sands-of-exile" className="game-card">
              <div className="game-card-image">
                <img src={SandsOfExileCover} alt="Sands of Exile" />
              </div>
              <div className="game-card-content">
                <div className="game-card-header">
                  <h3 className="game-card-title">Sands of Exile</h3>
                  <span className="game-year-chip">2024</span>
                </div>
                <p className="game-card-description">
                  Desert survival board game
                </p>
                <span className="game-card-play">Play →</span>
              </div>
            </Link>
          </div>

          <div className="video-gallery-header">
            <h3 className="video-gallery-title">Level Design & Cinematics</h3>
            <p className="video-gallery-subtitle">
              Experiments in Unreal Engine
            </p>
          </div>

          <div className="video-gallery">
            {gameDesignVideos.map((video, index) => (
              <div key={index} className="video-frame">
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </RevealSection>

        {/* ═══════════════════════════════════════════════════════════
           MUSIC - Songs & Compositions
           ═══════════════════════════════════════════════════════════ */}
        <RevealSection id="music" className="music-section">
          <div className="section-header">
            <span className="section-number">V</span>
            <h2 className="section-title">Songs & Music</h2>
            <p className="section-subtitle">
              Original compositions and soundtracks
            </p>
          </div>

          <div className="music-albums">
            <MusicPlayer albums={musicAlbums} />
          </div>
        </RevealSection>

        {/* ═══════════════════════════════════════════════════════════
           FOOTER
           ═══════════════════════════════════════════════════════════ */}
        <footer className="archive-footer">
          <div className="footer-emblem">◈</div>
          <p className="footer-text">The archive continues to grow...</p>
          <Link to="/ai-philosophy" className="ai-philosophy-link">
            My thoughts on AI & Creativity
          </Link>
          <div className="footer-contact">
            <span>mbehery@protonmail.com</span>
          </div>
        </footer>
      </div>

      {/* Fullscreen Image Viewer */}
      <AnimatePresence>
        {viewerOpen && (
          <ImageViewer
            images={filteredPaintings}
            currentIndex={currentImageIndex}
            onClose={() => setViewerOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreativeMode;
